import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from "react";
import AuthService from "../services/Auth.service";
import { setAuthToken } from "../services/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "ce_auth_token";
const USER_KEY = "ce_auth_user";
const EXPIRY_KEY = "ce_auth_expiry";

function loadStoredAuth() {
    const expiry = localStorage.getItem(EXPIRY_KEY);
    if (expiry) {
        if (Date.now() > Number(expiry)) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem(EXPIRY_KEY);
        } else {
            const token = localStorage.getItem(TOKEN_KEY);
            const raw = localStorage.getItem(USER_KEY);
            try {
                return { token, user: raw ? JSON.parse(raw) : null };
            } catch {
                return { token: null, user: null };
            }
        }
    }

    const token = sessionStorage.getItem(TOKEN_KEY);
    const raw = sessionStorage.getItem(USER_KEY);
    try {
        return { token, user: raw ? JSON.parse(raw) : null };
    } catch {
        return { token: null, user: null };
    }
}

function clearStoredAuth() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
}

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => loadStoredAuth().token);
    const [user, setUser] = useState(() => loadStoredAuth().user);
    const [verified, setVerified] = useState(null); // null = checking, true/false = done

    useEffect(() => {
        if (!token) {
            setVerified(false);
            return;
        }

        setAuthToken(token);

        AuthService.verify()
            .then((res) => {
                setUser(res.data);
                setVerified(true);
            })
            .catch(() => {
                clearStoredAuth();
                setAuthToken(null);
                setToken(null);
                setUser(null);
                setVerified(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (username, password, persistent = false) => {
        const res = await AuthService.login(username, password);
        const { token: newToken, user: newUser } = res.data;

        clearStoredAuth();

        if (persistent) {
            const expiresAt = Date.now() + 30 * 24 * 60 * 60 * 1000;
            localStorage.setItem(TOKEN_KEY, newToken);
            localStorage.setItem(USER_KEY, JSON.stringify(newUser));
            localStorage.setItem(EXPIRY_KEY, String(expiresAt));
        } else {
            sessionStorage.setItem(TOKEN_KEY, newToken);
            sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
        }

        setAuthToken(newToken);
        setToken(newToken);
        setUser(newUser);
        setVerified(true);
        return newUser;
    }, []);

    const logout = useCallback(async () => {
        try {
            await AuthService.logout();
        } catch {
            // Clear local state even if server call fails
        }
        clearStoredAuth();
        setAuthToken(null);
        setToken(null);
        setUser(null);
        setVerified(false);
    }, []);

    // Auto-logout when any API call returns 401 (token expired/revoked).
    useEffect(() => {
        const handler = () => { if (token) logout(); };
        window.addEventListener("auth:unauthorized", handler);
        return () => window.removeEventListener("auth:unauthorized", handler);
    }, [token, logout]);

    return (
        <AuthContext.Provider
            value={{
                token,
                user,
                login,
                logout,
                isAuthenticated: !!token && verified === true,
                isVerifying: verified === null,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
}
